import { useCallback } from 'react'

import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react'
import { useDispatch, useSelector } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'

import NoSearchResults from './NoSearchResults'
import SearchOption from './SearchOption'
import SearchOptions from './SearchOptions'
import SearchSelect from './SearchSelect'
import SearchSelectedTags from './SearchSelectedTags'
import {
  clearGroupOptions,
  loadGroupOptions,
  paginateGroupOptions,
} from '../../../redux/features/groupsSlice'
import {
  addLeadingZeroToDistrict,
  convertDistrictToNumeric,
  hasPermission,
} from '../../../utils'
import CreateGroupModal from '../forms/CreateGroupModal'

const ConstituentGroupSelect = ({
  onSelect = () => {},
  onClear = () => {},
  district,
  initialSelected,
  dropdownHeight,
  inputProps = {},
  preventCreate = false,
  showTags = true,
  onRemove = () => {},
}) => {
  const dispatch = useDispatch()

  const user = useSelector(state => state.auth.user)
  const groupOptions = useSelector(state => state.groups.options)
  const searchLoading = useSelector(state => state.groups.options_loading)

  // Modal Disclosure
  const { isOpen, onOpen, onClose } = useDisclosure()

  const filters = useSelector(state => state.directory.filters) || []

  const selectedDistrictFilter = filters.district__in

  const districtValue = user.is_admin
    ? district ?? undefined
    : addLeadingZeroToDistrict(user.district)

  const filterParams = {
    district__in: districtValue ? [districtValue] : selectedDistrictFilter,
    name: '',
  }

  const loadOptions = searchTerm => {
    dispatch(clearGroupOptions())
    dispatch(loadGroupOptions({ ...filterParams, name: searchTerm }))
  }

  const debouncedHandleChange = useDebouncedCallback(e => {
    loadOptions(e.target.value)
  }, 500)

  const loadMoreResults = useCallback(() => {
    dispatch(paginateGroupOptions())
  }, [])

  const filterOptions = useCallback(
    (searchTerm, selectedOptions) => {
      let options = groupOptions.filter(
        group => !selectedOptions.find(({ value }) => value.id === group.id)
      )

      if (searchTerm) {
        let lowerSearch = searchTerm.toLowerCase()
        options = options.filter(
          group =>
            group.name.toLowerCase().includes(lowerSearch) ||
            group.alias.toLowerCase().includes(lowerSearch)
        )
      }

      if (options.length === 0) return null
      return options
    },
    [groupOptions]
  )

  return (
    <>
      <Flex direction={{ base: 'column', sm: 'row' }} w='100%' align='center'>
        <Box w='100%'>
          <SearchSelect>
            <SearchOptions
              loadOptions={loadOptions}
              loadMoreOptions={loadMoreResults}
              onClear={onClear}
              onSearch={debouncedHandleChange}
              initialSelected={initialSelected}
              isLoading={searchLoading}
              dropdownHeight={dropdownHeight}
              inputProps={{
                placeholder: 'Select group(s) from dropdown',
                ...inputProps,
              }}
            >
              {({ searchTerm, selectedOptions }) =>
                filterOptions(searchTerm, selectedOptions)?.map(
                  (group, index) => (
                    <SearchOption
                      key={`group-option-${index}`}
                      option={{ label: group.name, value: group }}
                      onSelect={onSelect}
                    >
                      <Text>{group.name}</Text>
                      {hasPermission(
                        user,
                        'directory.constituent.view_all_constituents'
                      ) && (
                        <Text variant='helper'>
                          District {convertDistrictToNumeric(group.district)}
                        </Text>
                      )}
                    </SearchOption>
                  )
                ) || <NoSearchResults>No groups found</NoSearchResults>
              }
            </SearchOptions>
            {showTags && <SearchSelectedTags onRemove={onRemove} />}
          </SearchSelect>
        </Box>
        {!preventCreate && (
          <Button
            size='lg'
            fontSize='sm'
            onClick={onOpen}
            variant='outline'
            m={2}
          >
            Create new group
          </Button>
        )}
      </Flex>
      <CreateGroupModal
        isOpen={isOpen}
        onClose={onClose}
        onCreate={group => onSelect(group)}
      />
    </>
  )
}

export default ConstituentGroupSelect
